import { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import {
  activeOddsLevel,
  MOD_LABELS,
  type IframePoolOfferOdds,
  type VolatilityLevel,
} from '../../../../modules/api';
import { useMediaQuery } from '../../../../modules/shell';
import { OddsSheet } from '../OddsSheet';
import { OddsPopover } from '../OddsPopover';
import { StatsSummary } from './StatsSummary';
import { ViewOddsTrigger } from './ViewOddsTrigger';

export interface OddsPanelProps {
  odds: IframePoolOfferOdds;
  volatility: VolatilityLevel;
  onVolatilityChange: (level: VolatilityLevel) => void;
  /** Sold-out pack: the figures stay, picking odds is locked. */
  isLocked?: boolean;
}

/**
 * Modern OddsPanel — pixel-perfect Figma `1336:86230`.
 *
 * Glass stats card: Min Value · Max Pull (centre hero) · Select Odds. The
 * "Select Odds" button opens "Choose your pack style" (partner mockup) — a card
 * over the sidebar on desktop ({@link OddsPopover}) or a bottom sheet on
 * mobile ({@link OddsSheet}).
 *
 * Design Constructor surface name: `StatsRow`. Glass background / border /
 * blur flow through `--co-StatsRow-*` (see `index.css` `.stats-row-card`).
 */
export function OddsPanel({ odds, volatility, onVolatilityChange, isLocked = false }: OddsPanelProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  // Desktop ≥800px gets the anchored popover; below that, the bottom sheet.
  // This MUST be a JS mount/unmount, not a `hidden`/`block` wrapper: the sheet
  // portals to document.body, so a `display:none` ancestor can't hide it — on
  // desktop it would otherwise paint over the popover.
  //
  // Known 16px niggle: a viewport media query counts a classic scrollbar in
  // while the layout's `@[800px]` container query doesn't, so in the
  // ~800–816px window band the POPOVER opens over the still-mobile layout.
  // Both variants work there — it's only a presentation mismatch — and the
  // alternative is a JS width-observer hook, which isn't worth it here.
  const isDesktop = useMediaQuery('(min-width: 800px)');
  const level = activeOddsLevel(odds, volatility);
  const modLabel = MOD_LABELS[volatility];

  return (
    // `data-odds-popover-open` (desktop popover only) lets the containing
    // sidebar lift its stacking context above the sticky header while the
    // popover is up — see PackLayout's `<aside>`. The popover's own z-index is
    // trapped inside the sidebar's stacking context, so the lift has to happen
    // on the sidebar, not here. Static on desktop so the popover anchors to
    // the sidebar's top instead of this block.
    <div className="relative @[800px]:static" data-odds-popover-open={open && isDesktop ? true : undefined}>
      <section
        aria-label="Pack odds"
        data-co-component="StatsRow"
        className="
          stats-row-card
          relative
          @[800px]:overflow-hidden
          @[800px]:[border-radius:var(--co-StatsRow-borderRadius,28px)]
          @[800px]:shadow-[0_64px_34px_0_rgba(0,0,0,0.05)]
          @[800px]:@max-[1200px]:[--statsRow-sizeScale:0.85]
        "
      >
        <StatsSummary
          // 20px under the figures on mobile, where the block sits in the
          // fixed dock right above the buy row (partner Figma 2026-10-08).
          className="px-5 pt-4 pb-5 @[800px]:pb-4"
          minValue={level.minValue}
          maxPull={level.maxValue}
          rightSlot={
            <ViewOddsTrigger
              modLabel={modLabel}
              isOpen={open}
              disabled={isLocked}
              onClick={() => setOpen((v) => !v)}
            />
          }
        />
      </section>

      {/* Desktop (≥800px) — card over the sidebar. */}
      {isDesktop && (
        <AnimatePresence>
          {open && (
            <OddsPopover
              odds={odds}
              volatility={volatility}
              onVolatilityChange={onVolatilityChange}
              onClose={close}
            />
          )}
        </AnimatePresence>
      )}

      {/* Mobile (<800px) — bottom sheet sliding up (partner mockup). */}
      {!isDesktop && (
        <AnimatePresence>
          {open && (
            <OddsSheet
              odds={odds}
              volatility={volatility}
              onVolatilityChange={onVolatilityChange}
              onClose={close}
            />
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
